import { useCallback, useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useSelector, useDispatch } from 'react-redux'

import Footer from '../../footer'
import fetchAuthUserQuery from '../../common-services/fetch-auth-user'
import companiesQuery from '../AdminHome/services/companiesQuery'
import companyInfoQuery from './services/company-info'
import HomeHeader from './Components/HomeHeader'
import ControlPanel from './Components/ControlPanel'
import BackgroundCheckDataTable from './Components/BackgroundCheckDataTable'
import Overlay from '../../Components/Overlay'
import backgroundCheckList from './services/background-check-list'
import { hasPreCheckAccess, hasBackgroundCheckAccess } from '../../helpers/user-check-permissions'
import {
    setDraftFilter,
    applyFilters,
    resetDraft,
    clearFilters,
    setSelectedStatuses,
    setEpicOrderedStatus,
    setQaActionRequiredStatus,
    setSortConfig,
    setCurrentPage,
    setItemsPerPage,
} from './store/homeFiltersSlice'

// Filter inputs that sit behind the "Apply" button (batched). Everything else — status
// selection, the Epic / QA quick filters, sort and pagination — is an immediate control.
const BATCHED_KEYS = ['search', 'companyIds', 'excludeProdDemo', 'dateFrom', 'dateTo']

const HomePage = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const {
        draft,          // filter inputs being edited (behind Apply)
        applied,        // last-applied inputs that actually drive the query
        selectedStatuses,
        epicOrderedStatus,
        qaActionRequiredStatus,
        sortConfig,
        currentPage,
        itemsPerPage,
    } = useSelector((state) => state.homeFilters)

    const [activeTab, setActiveTab] = useState()
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

    // Single entry point for the child filter controls. Batched keys update the DRAFT
    // (no query until Apply); status / epic / QA keys are immediate.
    const updateFilter = useCallback((key, value) => {
        if (BATCHED_KEYS.includes(key)) {
            dispatch(setDraftFilter({ key, value }))
        } else if (key === 'selectedStatuses') {
            dispatch(setSelectedStatuses(value))
        } else if (key === 'epicOrderedStatus') {
            dispatch(setEpicOrderedStatus(value))
        } else if (key === 'qaActionRequiredStatus') {
            dispatch(setQaActionRequiredStatus(value))
        }
    }, [dispatch])

    const { data: dataAuth, error: errorAuth, isLoading: isLoadingAuth } = useQuery({
        queryKey: ['fetch-auth-user'],
        queryFn: () => fetchAuthUserQuery(),
    })

    const {
        data: company,
        error: companyError,
        isLoading: companyIsLoading,
    } = useQuery({
        queryKey: ['company'],
        queryFn: () => companyInfoQuery(),
        enabled: dataAuth?.role === "HR",
    })

    const {
        data: companyNameData,
        error: companyNameError,
        isLoading: companyNameIsLoading,
    } = useQuery({
        queryKey: ['companies'],
        queryFn: () => companiesQuery(),
        enabled: dataAuth?.role === 'ADMIN' && activeTab === 'background',
    })

    // Effective permissions for the HR user: the company-level permission AND the
    // user's own scope (grandfathered for legacy users). These drive both the home
    // landing and which tabs are shown.
    const canBackground = !!(company?.hasBackgroundCheckPermission && hasBackgroundCheckAccess(dataAuth?.scopes))
    const canPreCheck = !!(company?.hasPreCheckPermission && hasPreCheckAccess(dataAuth?.scopes))

    useEffect(() => {

        if (!dataAuth) {
            return
        }

        if (dataAuth?.role !== "HR") {
            setActiveTab('background')
            return
        }

        if (!company) {
            return
        }

        // HR landing is driven by effective permissions. Background stays on the home
        // route; a user who can only do TruChecks is sent to the "/pre-checks" page
        // (shared with QA); a user who can do neither gets a no-access state instead
        // of an empty background list.
        if (canBackground) {
            setActiveTab('background')
            return
        }

        if (canPreCheck) {
            navigate('/pre-checks', { replace: true })
            return
        }

        setActiveTab('none')
    }, [company, dataAuth, canBackground, canPreCheck, navigate])

    const companies = Array.isArray(companyNameData)
        ? companyNameData.map(item => ({
            companyId: item._id,
            companyName: item.name,
            createdAt: item.createdAt,
        }))
        : []

    // What the filter panel shows: DRAFT inputs (search / company / demo / dates) plus the
    // immediate status controls.
    const filters = useMemo(() => ({
        search: draft.search,
        companyIds: draft.companyIds,
        excludeProdDemo: draft.excludeProdDemo,
        dateFrom: draft.dateFrom,
        dateTo: draft.dateTo,
        selectedStatuses,
        epicOrderedStatus,
        qaActionRequiredStatus,
    }), [draft, selectedStatuses, epicOrderedStatus, qaActionRequiredStatus])

    // Whether the draft inputs differ from what's applied — drives the "unapplied changes"
    // hint and the Apply / Reset buttons.
    const isDirty = useMemo(
        () => JSON.stringify(draft) !== JSON.stringify(applied),
        [draft, applied],
    )

    const hasActiveFilters = !!(
        applied.search
        || (applied.companyIds && applied.companyIds.length)
        || applied.dateFrom
        || applied.dateTo
        || (selectedStatuses && selectedStatuses.length)
        || epicOrderedStatus
        || qaActionRequiredStatus
        || isDirty
    )

    // The request that drives the list — APPLIED inputs + the immediate status / epic / QA
    // controls. A DRAFT edit does not change this (only Apply does), so the query is not
    // re-run on every keystroke.
    const processedFilters = useMemo(() => {
        const statuses = selectedStatuses || []
        return {
            search: applied.search,
            companyIds: applied.companyIds,
            excludeProdDemo: applied.excludeProdDemo ?? (dataAuth?.role === 'ADMIN'),
            dateFrom: applied.dateFrom,
            dateTo: applied.dateTo,
            selectedStatuses: statuses.includes('Total') ? [] : statuses,
            epicOrderedStatus,
            qaActionRequiredStatus,
        }
    }, [applied, selectedStatuses, epicOrderedStatus, qaActionRequiredStatus, dataAuth?.role])

    const {
        data,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: ['background-checks', processedFilters, currentPage, itemsPerPage, sortConfig],
        queryFn: () => backgroundCheckList(processedFilters, currentPage, itemsPerPage, sortConfig),
        refetchOnWindowFocus: !isCreateModalOpen,
        enabled: activeTab === 'background',
    })

    const backgroundData = data?.result || []
    const startIndex = data?.startIndex || 0
    const endIndex = data?.endIndex || 0

    const navigateManageBackgroundCheckGroup = useCallback(() => {
        navigate('manage-background-check-group')
    }, [navigate])

    const navigateManageIndividualBackgroundChecks = useCallback(() => {
        navigate('manage-individual-background-check-group')
    }, [navigate])

    const navigatePreChecks = useCallback(() => {
        navigate('pre-checks')
    }, [navigate])

    // Background stays on the home route; Pre Checks is its own "/pre-checks"
    // page, so switching to that tab navigates there.
    const handleTabChange = useCallback((tab) => {
        if (tab === 'preChecks') {
            navigate('/pre-checks')
            return
        }
        setActiveTab('background')
    }, [navigate])

    // Until we know who is logged in (and, for HR, which permissions apply),
    // render only a splash screen. Rendering the default home during this
    // window flashed the wrong screen on slower machines before the redirect
    // to "/pre-checks" kicked in. Errors fall through so they stay visible.
    const isDecidingLanding = isLoadingAuth
        || (dataAuth?.role === 'HR' && !companyError && !activeTab)

    if (isDecidingLanding) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-gray-300 border-t-green-500 rounded-full animate-spin"></div>
            </div>
        )
    }

    // The background list renders for QA/ADMIN always, and for HR only when they've
    // landed on the Background tab (HR without background permission sees "none").
    const showBackgroundList = dataAuth?.role !== 'HR' || activeTab === 'background'

    return (
        <div className="min-h-screen bg-gray-50">
            <main className="px-4 sm:px-6 py-4 sm:py-8">
                {dataAuth?.role === 'HR' && (
                    <HomeHeader
                        activeTab={activeTab}
                        onTabChange={handleTabChange}
                        canBackground={canBackground}
                        canPreCheck={canPreCheck}
                    />
                )}

                {/* QA/ADMIN navigation actions (no per-list "New Order" for QA). */}
                {dataAuth?.role !== 'HR' && (
                    <div className="py-2 w-full">
                        <div className="flex flex-wrap justify-center gap-2 items-center px-2 sm:px-0">
                            <button
                                className="w-full sm:w-auto px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 font-medium text-sm"
                                onClick={navigateManageBackgroundCheckGroup}
                            >
                                Manage Background Check Groups
                            </button>
                            <button
                                className="w-full sm:w-auto px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 font-medium text-sm"
                                onClick={navigateManageIndividualBackgroundChecks}
                            >
                                Manage Individual Background Checks
                            </button>
                            <button
                                className="w-full sm:w-auto px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 font-medium text-sm"
                                onClick={navigatePreChecks}
                            >
                                TruCheck
                            </button>
                        </div>
                    </div>
                )}

                {dataAuth?.role === 'HR' && activeTab === 'none' && (
                    <div className="mt-10 text-center">
                        <div className="inline-block rounded-xl border border-gray-200 bg-white px-8 py-10 shadow-sm">
                            <h2 className="text-lg font-semibold text-gray-800 mb-1">No access</h2>
                            <p className="text-sm text-gray-500 max-w-sm">
                                You don’t have permission to view Background Checks or TruCheck.
                                Please contact your administrator.
                            </p>
                        </div>
                    </div>
                )}

                {showBackgroundList && (
                    <>
                        {(errorAuth || companyNameError) && (
                            <div className="text-center my-4 text-red-600 font-medium">
                                Error: {errorAuth?.message || companyNameError?.message}
                            </div>
                        )}

                        <ControlPanel
                            role={dataAuth?.role}
                            filters={filters}
                            updateFilter={updateFilter}
                            companies={companies}
                            statusCounts={data?.statusCounts}
                            epicCounts={data?.epicCounts}
                            qaCounts={data?.qaCounts}
                            isDirty={isDirty}
                            hasActiveFilters={hasActiveFilters}
                            onApply={() => dispatch(applyFilters())}
                            onReset={() => dispatch(resetDraft())}
                            onClear={() => dispatch(clearFilters())}
                        />

                        <BackgroundCheckDataTable
                            backgroundData={backgroundData}
                            role={dataAuth?.role}
                            refetch={refetch}
                            itemsPerPage={itemsPerPage}
                            setItemsPerPage={(val) => dispatch(setItemsPerPage(val))}
                            currentPage={currentPage}
                            setCurrentPage={(val) => dispatch(setCurrentPage(typeof val === 'function' ? val(currentPage) : val))}
                            startIndex={startIndex}
                            endIndex={endIndex}
                            sortConfig={sortConfig}
                            setSortConfig={(cfg) => dispatch(setSortConfig(cfg))}
                            totalDataCount={data?.totalCount}
                            isCreateModalOpen={isCreateModalOpen}
                            setIsCreateModalOpen={setIsCreateModalOpen}
                        />
                    </>
                )}
            </main>

            <Footer />

            {(isFetching || isLoadingAuth || companyNameIsLoading || companyIsLoading) && <Overlay />}
        </div>
    )
}

export default HomePage
